/** Netflix Subs Firebase v2 HTTP client (`https://netflixsubs.app/v2/...`). */

export const V2_BASE = 'https://netflixsubs.app';

export type V2Ok<T> = { ok: true; data: T };

export type V2Err = { ok: false; error: { code: string; message: string } };

export type V2Body<T> = V2Ok<T> | V2Err;

export function isV2Ok<T>(body: V2Body<T> | undefined | null): body is V2Ok<T> {
  return Boolean(body && body.ok === true);
}

export function isV2QuotaError(body: V2Body<unknown> | undefined | null): boolean {
  return Boolean(body && body.ok === false && body.error?.code === 'quota_exceeded');
}

export function isV2MemberOnly(body: V2Body<unknown> | undefined | null): boolean {
  if (!body || body.ok !== false) return false;
  const code = body.error?.code ?? '';
  const msg = (body.error?.message ?? '').toLowerCase();
  return code === 'unauthorized' && msg.includes('member');
}

export function isV2Duplicate(body: V2Body<unknown> | undefined | null): boolean {
  return Boolean(body && body.ok === false && body.error?.code === 'duplicate');
}

export function v2IdAuth(userId: string, userToken: string) {
  return {
    id: userId || 'public',
    token: userToken || 'public',
  };
}

function buildUrl(
  path: string,
  query?: Record<string, string | number | boolean | undefined | null>,
): string {
  const url = new URL(path.startsWith('http') ? path : `${V2_BASE}${path}`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

export type V2FetchOptions = {
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
  token?: string;
  fetchImpl?: typeof fetch;
};

export async function v2Fetch<T>(
  method: string,
  path: string,
  opts: V2FetchOptions = {},
): Promise<V2Body<T>> {
  const fetchImpl = opts.fetchImpl ?? fetch.bind(globalThis);
  try {
    const headers: Record<string, string> = {
      Accept: 'application/json',
    };

    const bodyObj =
      opts.body && typeof opts.body === 'object' && opts.body !== null
        ? (opts.body as Record<string, unknown>)
        : null;

    const token = String(
      opts.token ??
        opts.query?.token ??
        (bodyObj && bodyObj.token != null ? bodyObj.token : '') ??
        '',
    );
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const query = opts.query ? { ...opts.query } : undefined;
    if (query && 'token' in query) {
      delete query.token;
    }

    const init: RequestInit = { method, headers };
    if (opts.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(opts.body);
    }
    const response = await fetchImpl(buildUrl(path, query), init);
    let body: unknown;
    try {
      body = await response.json();
    } catch (e) {
      console.log(e);
      return {
        ok: false,
        error: { code: 'network', message: `invalid_json_${response.status}` },
      };
    }
    if (
      body &&
      typeof body === 'object' &&
      ((body as V2Body<unknown>).ok === true || (body as V2Body<unknown>).ok === false)
    ) {
      return body as V2Body<T>;
    }
    if (!response.ok) {
      return {
        ok: false,
        error: { code: 'network', message: `http_${response.status}` },
      };
    }
    return {
      ok: false,
      error: { code: 'network', message: 'invalid_envelope' },
    };
  } catch (e) {
    console.log(e);
    return {
      ok: false,
      error: {
        code: 'network',
        message: String((e as { message?: string })?.message || e),
      },
    };
  }
}
